import { useCallback, useEffect, useState } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import "./TrendsPage.css";
import { getSalaryComparisonSingleRole } from "./api";
import { parseNum, fmt, fmtPct, payChangeClass, parseReviewPeriod, FORECAST } from "./trendsFormat";
import TrendsMixedChart from "./TrendsMixedChart";
import TrendsSearchSelect from "./TrendsSearchSelect";
import SalaryBreakdownDialog from "./SalaryBreakdownDialog";

/**
 * TrendsSingleRole - "Single Role" tab.
 *
 * React port of single-role.component.ts: one role in one territory over
 * the year window. KPI cards (cumulative pay change / cumulative inflation /
 * real wage change / latest salary), mixed bar+line chart (salary bars with
 * forecast amber, solid pay-change line, dashed inflation line), period
 * breakdown table, Excel export - all formulas and colors carried over
 * verbatim (see trendsFormat.js for the shared helpers).
 */
export default function TrendsSingleRole({ startYear, endYear, startMonth, jobTitles, territories }) {
  const [territoryId, setTerritoryId] = useState(null);
  const [jobCode, setJobCode] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [breakdown, setBreakdown] = useState(null);

  // Default territory = first option, matching the old component's
  // `selectedTerritoryId = territoryOptions[0].id`.
  useEffect(() => {
    if (territoryId == null && territories.length > 0) setTerritoryId(territories[0].id);
  }, [territories, territoryId]);

  // Load whenever every required filter is set; window changes from the
  // shell re-fire automatically (old ngOnChanges behavior).
  const canLoad = territoryId && jobCode && startYear && endYear;
  const load = useCallback(() => {
    if (!canLoad) return;
    setLoading(true);
    getSalaryComparisonSingleRole({ startYear, endYear, startMonth, territoryId, minervaJobCode: jobCode })
      .then((data) => {
        setRows(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => {
        setRows([]);
        setLoading(false);
      });
  }, [canLoad, startYear, endYear, startMonth, territoryId, jobCode]);

  useEffect(() => {
    load();
  }, [load]);

  const hasData = rows.length > 0;
  const hasForecast = rows.some((r) => r.is_forecasted_data === true);

  // computeKpis() - verbatim port.
  let cumPay = 0, cumInf = 0, payCount = 0, infCount = 0, reviewCycleCount = 0;
  rows.forEach((row) => {
    const pay = parseNum(row.pay_change);
    const inf = parseNum(row.inflation);
    if (pay !== null) { cumPay += pay; payCount++; reviewCycleCount++; }
    if (inf !== null) { cumInf += inf; infCount++; }
  });
  const cumulativePayChange = payCount > 0 ? cumPay : null;
  const cumulativeInflation = infCount > 0 ? cumInf : null;
  const realWageChange =
    cumulativePayChange !== null && cumulativeInflation !== null
      ? cumulativePayChange - cumulativeInflation
      : null;
  const firstRow = rows[0];
  const latestRow = rows[rows.length - 1];

  const territoryName = territories.find((t) => t.id === territoryId)?.name || "";
  const roleName = jobTitles.find((j) => j.job_code === jobCode)?.name || jobCode || "";

  function openBreakdown(row) {
    const { startMon, endMon, fullStartYear, fullEndYear } = parseReviewPeriod(row, startYear, endYear);
    setBreakdown({
      minervaJobCode: jobCode,
      territoryId,
      startMonth: startMon,
      startYear: fullStartYear,
      endMonth: endMon,
      endYear: fullEndYear,
      label: `${roleName} — ${territoryName} (${row.review_period})`,
    });
  }

  async function exportExcel() {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Single Role Analysis");
    ws.columns = [
      { header: "Review Period", key: "period", width: 18 },
      { header: "Salary (£)", key: "salary", width: 14, style: { numFmt: '£#,##0.00' } },
      { header: "Pay Change", key: "pay", width: 12, style: { numFmt: '0.0"%"' } },
      { header: "Inflation", key: "inflation", width: 12, style: { numFmt: '0.0"%"' } },
      { header: "Real Change", key: "real", width: 12, style: { numFmt: '0.0"%"' } },
      { header: "Forecast", key: "forecast", width: 10 },
    ];
    const head = ws.getRow(1);
    head.font = { bold: true, color: { argb: "FFFFFFFF" } };
    head.eachCell((c) => {
      c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F4E78" } };
    });
    rows.forEach((r) => {
      const row = ws.addRow({
        period: r.review_period,
        salary: parseNum(r.salary),
        pay: parseNum(r.pay_change),
        inflation: parseNum(r.inflation),
        real: parseNum(r.real_change),
        forecast: r.is_forecasted_data ? "Yes" : "No",
      });
      if (r.is_forecasted_data) {
        row.eachCell((c) => {
          c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFF3CD" } };
        });
      }
    });
    ws.eachRow((row) => {
      row.eachCell((c) => {
        c.border = {
          top: { style: "thin" }, bottom: { style: "thin" },
          left: { style: "thin" }, right: { style: "thin" },
        };
      });
    });
    const buffer = await wb.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      `Single-Role_${territoryName}_${roleName}.xlsx`.replace(/\s+/g, "-")
    );
  }

  return (
    <div className="tr-tab">
      <div className="tr-mode-eyebrow">One role &times; one country vs inflation</div>
      <div className="tr-filters">
        <label className="tr-field">
          <span className="tr-field-label">Country<span className="tr-req">*</span></span>
          <select className="tr-select" value={territoryId ?? ""} onChange={(e) => setTerritoryId(Number(e.target.value))}>
            {territories.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </label>
        <label className="tr-field tr-field-role">
          <span className="tr-field-label">Role<span className="tr-req">*</span></span>
          <TrendsSearchSelect
            options={jobTitles.map((j) => ({ value: j.job_code, label: j.name, sub: j.sub_stack }))}
            value={jobCode}
            placeholder="Select role"
            onChange={setJobCode}
          />
        </label>
        {hasData && (
          <button type="button" className="tr-export-btn" onClick={exportExcel}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 3v18" /></svg>
            Export to Excel
          </button>
        )}
      </div>

      {!hasData && !loading && (
        <div className="tr-empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 17l6-6 4 4 8-8" /><path d="M15 7h6v6" /></svg>
          <p>Select a country and role above to view analysis.</p>
        </div>
      )}
      {loading && <p className="tr-status">Loading…</p>}

      {hasData && !loading && (
        <>
          <div className="tr-kpis">
            <div className="tr-kpi">
              <div className="tr-kpi-label">CUMULATIVE PAY CHANGE</div>
              <div className={`tr-kpi-value ${payChangeClass(cumulativePayChange)}`}>{fmtPct(cumulativePayChange)}</div>
              <div className="tr-kpi-sub">{reviewCycleCount} review cycles</div>
            </div>
            <div className="tr-kpi">
              <div className="tr-kpi-label">CUMULATIVE INFLATION</div>
              <div className="tr-kpi-value">{fmtPct(cumulativeInflation)}</div>
              <div className="tr-kpi-sub">Same window</div>
            </div>
            <div className="tr-kpi">
              <div className="tr-kpi-label">REAL WAGE CHANGE</div>
              <div className={`tr-kpi-value ${payChangeClass(realWageChange)}`}>{fmtPct(realWageChange)}</div>
              <div className="tr-kpi-sub">Pay change − inflation</div>
            </div>
            <div className="tr-kpi">
              <div className="tr-kpi-label">SALARY {latestRow?.review_period}</div>
              <div className="tr-kpi-value">{fmt(latestRow?.salary)}</div>
              <div className="tr-kpi-sub">From {fmt(firstRow?.salary)} in {firstRow?.review_period}</div>
            </div>
          </div>

          {hasForecast && (
            <div className="tr-forecast-banner">
              Window includes forecast data. Amber-shaded rows &amp; regions are projected.
              Sources: BoE / ECB / Fed / IMF / RBI / SARB / BCB.
            </div>
          )}

          <TrendsMixedChart
            labels={rows.map((r) => r.review_period)}
            forecastFlags={rows.map((r) => !!r.is_forecasted_data)}
            bars={[
              {
                label: "Salary",
                fill: "rgba(102,195,208,0.60)",
                border: "#66c3d0",
                forecastFill: FORECAST.barFillStrong,
                values: rows.map((r) => parseNum(r.salary)),
              },
            ]}
            lines={[
              // Pay change plots null as 0 (line always draws, old-chart
              // behavior); tooltip still shows an em dash via `raw`.
              {
                label: "Pay change %", color: "#2C6CB7", dash: null,
                values: rows.map((r) => parseNum(r.pay_change) ?? 0),
                raw: rows.map((r) => parseNum(r.pay_change)),
              },
              { label: "Inflation %", color: "#E4572E", dash: [5, 3], values: rows.map((r) => parseNum(r.inflation)) },
            ]}
            height={320}
          />

          <div className="tr-table-wrap">
            <div className="tr-table-title">Period breakdown</div>
            <table className="tr-table">
              <thead>
                <tr>
                  <th>REVIEW PERIOD</th>
                  <th>SALARY (£)</th>
                  <th>PAY CHANGE</th>
                  <th>INFLATION</th>
                  <th>REAL CHANGE</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className={r.is_forecasted_data ? "tr-forecast-row" : ""}>
                    <td>
                      {r.review_period}
                      {r.is_forecasted_data && <span className="tr-f-badge" title="Forecast">F</span>}
                    </td>
                    <td>
                      {fmt(r.salary)}
                      <button type="button" className="tr-info-btn" aria-label="Salary breakdown" onClick={() => openBreakdown(r)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>
                      </button>
                    </td>
                    <td className={payChangeClass(r.pay_change)}>{fmtPct(r.pay_change)}</td>
                    <td>{parseNum(r.inflation) === null ? "—" : `${parseNum(r.inflation)}%`}</td>
                    <td className={payChangeClass(r.real_change)}>{fmtPct(r.real_change)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <SalaryBreakdownDialog open={!!breakdown} request={breakdown} onClose={() => setBreakdown(null)} />
    </div>
  );
}
